// PricingDisplay — price with optional crossed-out original, recurring label.
// Inline styles only — no Tailwind. Colors read from the active TokenSet via
// useEmbedTokens() (Phase-0 primitives migration); rendered inside the embed's
// <EmbedThemeProvider>.

import { useEmbedTokens } from "../../embed-shared/primitives";
import { formatCurrency } from "../../embed-shared/currency";
import { formatProductPriceFrom } from "./product-display";

export interface PricingDisplayProps {
  amount: number;
  currency?: string;
  originalAmount?: number | null;
  recurringLabel?: string | null;
  /**
   * When true, the amount is the minimum of a variant/range and is prefixed
   * with "From" (TD-308). Flat prices render as-is ($0.00 / $X.XX).
   */
  isFromPrice?: boolean;
}

export function PricingDisplay({
  amount,
  currency = "USD",
  originalAmount,
  recurringLabel,
  isFromPrice = false,
}: PricingDisplayProps) {
  const t = useEmbedTokens();
  const hasDiscount = originalAmount != null && originalAmount > amount;
  // TD-308: the shown amount is a range minimum → render "From $X.XX". A
  // strike-through original still renders the crossed-out full price first.
  const priceLabel = isFromPrice ? formatProductPriceFrom(amount, currency) : formatCurrency(amount, currency);

  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: t.spacing.sm, flexWrap: "wrap" }}>
      {hasDiscount && (
        <span
          style={{
            fontSize: t.fontSize.sm,
            color: t.colors.priceOriginal,
            textDecoration: "line-through",
          }}
          aria-label={`Original price ${formatCurrency(originalAmount, currency)}`}
        >
          {formatCurrency(originalAmount, currency)}
        </span>
      )}
      <span
        style={{
          fontSize: t.fontSize.lg,
          fontWeight: 700,
          color: t.colors.text,
        }}
      >
        {priceLabel}
      </span>
      {recurringLabel && (
        <span
          style={{
            fontSize: t.fontSize.xs,
            color: t.colors.chargeRecurring,
            fontWeight: 500,
          }}
        >
          {recurringLabel}
        </span>
      )}
    </div>
  );
}
